import * as FilterHelper from './FilterHelper';
import { viewActions } from '../state/modules/view';
import { getPaginatedQueryFields } from '../graphql/schema/getQueryFields';
import { getAvailableColumns } from '../components/view/availableColumnsUtil';

import { useMemo } from 'react';
import { IWorkspaceColumnOverride, IWorkspaceView } from '../@types/workspaceTypes';
import { Direction, IFilterInput, Maybe } from '../graphql/schema/p4types';
import { ICustomColumn } from './setColumnsFromArray';
import { DocumentNode } from '@apollo/client';
import { IAvailableColumn } from '../components/view/ColumnSelector';
import { IEditableDataGridColumnDef } from '../@types/editableDataGridTypes';

export const FILTER_TYPE_MAP = {
  String: 'filterStrings',
  Boolean: 'filterBooleans',
  Object: 'filterObjects',
  Enum: 'filterEnums',
  LocalDate: 'filterDates',
  LocalDateTime: 'filterDateTimes',
  OffsetDateTime: 'filterDateTimes',
  Long: 'filterLongs',
  Int: 'filterLongs',
  BigDecimal: 'filterBigDecimals',
  TEXT: 'filterAttributeStrings',
  REFERENCE: 'filterAttributeObjects',
  INTEGER: 'filterAttributeLongs',
  NUMERIC: 'filterAttributeBigDecimals',
  DATE: 'filterAttributeDates',
  GRN: 'filterAttributeStrings',
};

export function moveColumn(columns, fieldName, newIndex) {
  // Find index for column relating to field being moved
  const from = columns.findIndex((column) => {
    return column.fieldName === fieldName;
  });
  if (from === newIndex) {
    return columns;
  }

  // Shuffle intervening columns and move requested column to new index
  const column = columns[from];
  const direction = from > newIndex ? -1 : 1;
  const newColumns = columns.slice();

  for (let index = from; index !== newIndex; index += direction) {
    newColumns[index] = Object.assign({}, columns[index + direction], {
      position: index,
    });
  }
  newColumns[newIndex] = Object.assign({}, column, { position: newIndex });

  return newColumns;
}

// Get map of field name to column details from available columns
export function getFieldNameToColumnMap(availableColumns: Array<IAvailableColumn>) {
  const fieldNameMap: { [fieldName: string]: IAvailableColumn } = {};
  availableColumns.forEach((column) => {
    if (column.fieldName) {
      fieldNameMap[column.fieldName] = column;
    }
  });

  return fieldNameMap;
}

export function getView(
  {
    viewScreenName,
    location,
    predefinedFilters,
    embeddedView = false,
    views,
    altView: altViewFromProps,
    additionalVariables,
  },
  screen,
  predefinedFilter: Array<IFilterInput> = [],
  altView,
  company,
  availableColumns: Array<IAvailableColumn>
) {
  let screenName = viewScreenName || screen || location.pathname.slice(1);
  if (screenName.endsWith('/')) screenName = screenName.slice(0, -1);

  const presetFilters = [].concat.apply([], [predefinedFilter, predefinedFilters || []]) as Array<IFilterInput>;

  //Note - Incomplete type.
  type IPotentialView = {
    orderBy: Array<{ fieldName: string; isDescending: boolean }>;
    columns?: Array<{ total: number; fieldName: string }>;
  };

  let foundView: IPotentialView | null = null;
  if (altView) {
    foundView = altView;
  } else if (views?.[screenName]) {
    foundView = altViewFromProps ? views[screenName].view[altViewFromProps] : views[screenName].currentView;
  }

  let orderBy = 'id';
  let orderDirection = Direction.Asc;
  let filters: Array<IFilterInput>;
  let totalColumns: Array<string> = [];

  if (foundView) {
    if (foundView.orderBy[0]) {
      orderBy = foundView.orderBy[0].fieldName;
      orderDirection = foundView.orderBy[0].isDescending ? Direction.Desc : Direction.Asc;
    }

    if (foundView?.columns) {
      totalColumns = foundView.columns.filter((col) => col.total).map((col) => col.fieldName);
    }

    filters = FilterHelper.getFilters(foundView, presetFilters, company, availableColumns ?? []);
  } else {
    filters = presetFilters;
  }

  const workspaceView: IWorkspaceView = {
    orderBy: orderBy,
    orderDirection: orderDirection,
    filters: filters,
    embeddedView: embeddedView,
    additionalVariables: additionalVariables || {},
    totalColumns: totalColumns,
    skip: !foundView && !views?.screenName,
    columns: foundView?.columns,
  };
  return workspaceView;
}

type ICreateDefaultViewConfigInput = {
  columnOverrides: IWorkspaceColumnOverride;
  initialSortColumn: string;
  initialSortDir: Direction;
  dashboard: boolean;
  viewScreenName: string;
  defaultTotalColumns?: Array<string>;

  /**
   * Don't believe key: string is ever used (legacy code)
   */
  columns: Array<{ key: string }> | Array<string>;
  miniGrid: boolean;
};

export const createDefaultViewConfig = (
  {
    columnOverrides: columnOverrides1,
    initialSortColumn,
    initialSortDir,
    dashboard,
    viewScreenName,
    columns = [],
    miniGrid,
    defaultTotalColumns,
  }: ICreateDefaultViewConfigInput,
  defaultFilter: string
) => {
  const columnOverrides = columnOverrides1;
  // Get initial sort column - if none specified use first column for grid table view
  let orderColumn: string = initialSortColumn;
  if (!orderColumn) {
    orderColumn =
      (columns as Array<{ key: string }>).find((columnMeta) => {
        return !!columnMeta.key;
      })?.key ?? 'id';
  }

  const orderBy = [
    {
      fieldName: orderColumn,
      isDescending: initialSortDir === Direction.Desc,
    },
  ];

  const visibleFieldNames = (miniGrid && dashboard) || columns;

  const view: {
    name: string;
    columnOverrides: Array<object>;
    visibleFieldNames: any;
    orderBy: Array<{ fieldName: string; isDescending: boolean }>;
    screen: string; //ViewScreenName
    genericFilter?: string;
    defaultTotalColumns?: Array<string>;
  } = {
    name: 'default',
    columnOverrides,
    visibleFieldNames,
    orderBy,
    screen: viewScreenName,
    defaultTotalColumns,
  };

  if (defaultFilter) {
    view.genericFilter = defaultFilter;
  }

  return view;
};

export const initializeViews = (
  views,
  viewScreenName,
  {
    columnOverrides,
    initialSortColumn,
    initialSortDir,
    dashboard,
    columns,
    miniGrid,
    hasDynamicColumns,
    availableColumns,
    location,
    dispatch,
    defaultTotalColumns,
  }
) => {
  const defaultFilter = location.state?.genericFilter;

  const defaultViewConfig = createDefaultViewConfig(
    {
      columnOverrides,
      initialSortColumn,
      initialSortDir,
      dashboard,
      viewScreenName,
      columns,
      miniGrid,
      defaultTotalColumns,
    },
    defaultFilter
  );

  if (defaultFilter || !(views && views.length > 0)) {
    dispatch(
      viewActions.initializeViews({
        screenName: viewScreenName,
        defaultViewConfig: defaultViewConfig,
        columnOverrides: columnOverrides,
        resetDefaultView: hasDynamicColumns,
        availableColumns: availableColumns,
        views: views,
      })
    );
    return; //If a default filter has been provided, do not switch to another view, just use the default view.
  }

  const altView = location?.state?.view;
  const alternateView =
    altView &&
    views.find((view) => {
      return view.name === altView;
    });
  const primaryView = views.find((view) => {
    return view.primaryView === true;
  });

  const initialView = alternateView || primaryView;

  dispatch(
    viewActions.initializeViews({
      screenName: viewScreenName,
      defaultViewConfig: defaultViewConfig,
      columnOverrides: columnOverrides,
      resetDefaultView: hasDynamicColumns,
      availableColumns: availableColumns,
      views: views,
      viewName: initialView?.name,
    })
  );
};

export const useAvailableColumns = (
  query: DocumentNode,
  entity: { workspace?: DocumentNode; type: string },
  {
    embeddedView,
    additionalVariables,
    includeValidation = true,
    customColumns, //DO NOT DEFAULT HERE - Used as memo dependency.
    columnOverrides,
  }: {
    embeddedView?: boolean;
    additionalVariables?: any;
    includeValidation?: boolean;
    customColumns?: Maybe<{ [key: string]: ICustomColumn }>;
    columnOverrides: IWorkspaceColumnOverride;
  }
): Array<IAvailableColumn> | undefined => {
  //Should only need to run this once, but can add dependencies to the memo if needed.
  return useMemo(() => {
    const exclusions = additionalVariables
      ? Object.keys(additionalVariables).filter((key) => key.includes('exclude'))
      : [];

    const queryFields = getPaginatedQueryFields(
      query || entity.workspace,
      entity.type,
      embeddedView,
      exclusions,
      false,
      {
        includeValidation,
      }
    );

    const availableColumnsAsObject: { [key: string]: ICustomColumn } = Object.assign(
      {},
      queryFields,
      customColumns ?? ({} as { [key: string]: ICustomColumn })
    );

    return getAvailableColumns(availableColumnsAsObject as any, columnOverrides);
  }, [customColumns, columnOverrides]);
};
